import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { axiosInstance } from "../App";
import { 
  CreditCard, 
  Building, 
  CheckCircle2, 
  Loader2, 
  Calendar,
  TrendingUp,
  AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from 'react-i18next';

function BillingSubscription() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("credit_card");
  const [processing, setProcessing] = useState(false);
  
  const [cardForm, setCardForm] = useState({
    cardHolder: "",
    cardNumber: "",
    expiryMonth: "",
    expiryYear: "",
    cvc: ""
  });
  
  const [bankForm, setBankForm] = useState({
    bankName: "",
    iban: "",
    reference: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const companyId = user.company_id;
      
      if (!companyId) {
        toast.error(t('billing.noCompany'));
        return;
      }
      
      // Fetch plans
      const plansRes = await axiosInstance.get("/api/payments/plans");
      setPlans(plansRes.data.plans || []);
      
      // Fetch current subscription
      const subRes = await axiosInstance.get(`/api/subscriptions/company/${companyId}`);
      setCurrentSubscription(subRes.data.subscription || null);
      
      // Fetch payment history
      const paymentsRes = await axiosInstance.get(`/api/payments/user/${user.id}`);
      setPayments(paymentsRes.data.payments || []);
    } catch (error) {
      console.error("Error fetching billing data:", error);
      toast.error(t('billing.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedPlan) return;
    
    setProcessing(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      
      if (paymentMethod === "credit_card") {
        // Validate card form
        if (!cardForm.cardHolder || !cardForm.cardNumber || !cardForm.expiryMonth || !cardForm.expiryYear || !cardForm.cvc) {
          toast.error(t('billing.fillAllFields'));
          return;
        }
        
        // Mock payment request
        const paymentData = {
          user_id: user.id,
          amount: selectedPlan.price,
          currency: "TRY",
          payment_method: {
            type: "credit_card",
            card_holder_name: cardForm.cardHolder,
            card_number: cardForm.cardNumber.replace(/\s/g, ""),
            card_expiry_month: cardForm.expiryMonth,
            card_expiry_year: cardForm.expiryYear,
            card_cvc: cardForm.cvc
          },
          plan_name: selectedPlan.name
        };
        
        const res = await axiosInstance.post("/api/payments/mock-charge", paymentData);
        
        if (res.data.status === "success") {
          // Create subscription
          await createSubscription(paymentData.payment_method);
          toast.success(t('billing.paymentSuccess'));
        } else {
          toast.error(t('billing.paymentFailed'));
        }
      } else {
        // Bank transfer
        if (!bankForm.bankName || !bankForm.iban || !bankForm.reference) {
          toast.error(t('billing.fillAllFields'));
          return;
        }
        
        const paymentData = {
          user_id: user.id,
          amount: selectedPlan.price,
          currency: "TRY",
          payment_method: {
            type: "bank_transfer",
            bank_name: bankForm.bankName,
            iban: bankForm.iban,
            reference_number: bankForm.reference
          },
          plan_name: selectedPlan.name
        };
        
        await axiosInstance.post("/api/payments/bank-transfer", paymentData);
        
        // Create subscription
        await createSubscription(paymentData.payment_method);
        
        toast.success(t('billing.bankTransferCreated'));
      }
      
      setShowPaymentDialog(false);
      fetchData();
      resetForms();
    } catch (error) {
      console.error("Payment error:", error);
      toast.error(error.response?.data?.detail || t('billing.paymentError'));
    } finally {
      setProcessing(false);
    }
  };

  const createSubscription = async (paymentMethod) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    
    await axiosInstance.post("/api/subscriptions/create", {
      user_id: user.id,
      plan_id: selectedPlan.id,
      payment_method: paymentMethod,
      auto_renew: true
    });
  };

  const handleChangePlan = async (newPlan) => {
    if (!currentSubscription) {
      // No subscription, create new one
      setSelectedPlan(newPlan);
      setShowPaymentDialog(true);
      return;
    }
    
    // Plan change
    setSelectedPlan(newPlan);
    setShowPaymentDialog(true);
  };

  const handleCancelSubscription = async () => {
    if (!currentSubscription || !currentSubscription.id) return;
    
    if (!window.confirm(t('billing.confirmCancel'))) return;
    
    try {
      await axiosInstance.post(`/api/subscriptions/${currentSubscription.id}/cancel`);
      toast.success(t('billing.subscriptionCancelled'));
      fetchData();
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error(t('billing.cancelError'));
    }
  };

  const handleToggleAutoRenew = async (value) => {
    if (!currentSubscription || !currentSubscription.id) return;
    
    try {
      await axiosInstance.patch(`/api/subscriptions/${currentSubscription.id}`, {
        auto_renew: value
      });
      toast.success(t('billing.autoRenewUpdated'));
      fetchData();
    } catch (error) {
      console.error("Auto-renew error:", error);
      toast.error(t('billing.autoRenewError'));
    }
  };

  const resetForms = () => {
    setCardForm({
      cardHolder: "",
      cardNumber: "",
      expiryMonth: "",
      expiryYear: "",
      cvc: ""
    });
    setBankForm({
      bankName: "",
      iban: "",
      reference: ""
    });
  };

  const formatCardNumber = (value) => {
    const cleaned = value.replace(/\s/g, "");
    const chunks = cleaned.match(/.{1,4}/g);
    return chunks ? chunks.join(" ") : cleaned;
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: "default",
      pending: "secondary",
      cancelled: "destructive",
      success: "default",
      failed: "destructive"
    };
    
    const statusLabels = {
      active: t('billing.statusActive'),
      pending: t('billing.statusPending'),
      cancelled: t('billing.statusCancelled'),
      success: t('billing.statusSuccess'),
      failed: t('billing.statusFailed')
    };
    
    return <Badge variant={variants[status] || "secondary"}>{statusLabels[status] || status}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.currentPlan')}</CardTitle>
          <CardDescription>{t('billing.currentPlanDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {currentSubscription ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">{currentSubscription.plan_name}</h3>
                  <p className="text-muted-foreground">
                    {currentSubscription.billing_amount} {currentSubscription.billing_currency} / {currentSubscription.billing_interval === "monthly" ? t('billing.month') : t('billing.year')}
                  </p>
                </div>
                {getStatusBadge(currentSubscription.status)}
              </div>
              
              {currentSubscription.next_billing_date && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>{t('billing.nextBilling')}: {new Date(currentSubscription.next_billing_date).toLocaleDateString('tr-TR')}</span>
                </div>
              )}
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Label htmlFor="auto-renew">{t('billing.autoRenew')}</Label>
                  <Switch
                    id="auto-renew"
                    checked={currentSubscription.auto_renew}
                    onCheckedChange={handleToggleAutoRenew}
                  />
                </div>
                
                {currentSubscription.status === "active" && (
                  <Button variant="destructive" size="sm" onClick={handleCancelSubscription}>
                    {t('billing.cancelSubscription')}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">{t('billing.noSubscription')}</p>
              <Button onClick={() => handleChangePlan(plans.find(p => p.id === "professional"))}>
                {t('billing.upgradePlan')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available Plans */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.availablePlans')}</CardTitle>
          <CardDescription>{t('billing.availablePlansDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {plans.map((plan) => (
              <Card key={plan.id} className={`${currentSubscription?.plan_id === plan.id ? 'border-primary' : ''}`}>
                <CardHeader>
                  <CardTitle>{plan.name}</CardTitle>
                  <div className="text-3xl font-bold">
                    {plan.price === 0 ? t('billing.free') : `₺${plan.price}`}
                    {plan.price > 0 && <span className="text-sm font-normal text-muted-foreground"> / {plan.billing_interval === "monthly" ? t('billing.month') : t('billing.year')}</span>}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-4">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  {currentSubscription?.plan_id === plan.id ? (
                    <Button disabled className="w-full">{t('billing.currentPlan')}</Button>
                  ) : (
                    <Button 
                      className="w-full" 
                      variant={plan.id === "professional" ? "default" : "outline"}
                      onClick={() => handleChangePlan(plan)}
                      disabled={plan.id === "free"}
                    >
                      {plan.id === "enterprise" ? t('billing.contactSales') : t('billing.selectPlan')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.paymentHistory')}</CardTitle>
          <CardDescription>{t('billing.paymentHistoryDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          {payments.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t('billing.noPayments')}</p>
          ) : (
            <div className="space-y-3">
              {payments.map((payment) => (
                <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {payment.payment_method_type === "credit_card" ? (
                      <CreditCard className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <Building className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div>
                      <p className="font-medium">{payment.plan_name || t('billing.payment')}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(payment.created_at).toLocaleDateString('tr-TR')}
                        {payment.card_last_four && ` • **** ${payment.card_last_four}`}
                        {payment.bank_name && ` • ${payment.bank_name}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">₺{payment.amount}</span>
                    {getStatusBadge(payment.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('billing.completePayment')}</DialogTitle>
            <DialogDescription>
              {selectedPlan && `${selectedPlan.name} - ₺${selectedPlan.price}`}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="credit_card">
                <CreditCard className="h-4 w-4 mr-2" />
                {t('billing.creditCard')}
              </TabsTrigger>
              <TabsTrigger value="bank_transfer">
                <Building className="h-4 w-4 mr-2" />
                {t('billing.bankTransfer')}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="credit_card" className="space-y-4">
              <div className="space-y-2">
                <Label>{t('billing.cardHolder')}</Label>
                <Input
                  placeholder={t('billing.cardHolderPlaceholder')}
                  value={cardForm.cardHolder}
                  onChange={(e) => setCardForm({...cardForm, cardHolder: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('billing.cardNumber')}</Label>
                <Input
                  placeholder="1234 5678 9012 3456"
                  value={formatCardNumber(cardForm.cardNumber)}
                  onChange={(e) => setCardForm({...cardForm, cardNumber: e.target.value.replace(/\s/g, "")})}
                  maxLength={19}
                />
                <p className="text-xs text-muted-foreground">
                  {t('billing.testCards')}: 5528790000000008 ({t('billing.success')}), 5406676000000008 ({t('billing.failed')})
                </p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{t('billing.expiryMonth')}</Label>
                  <Select value={cardForm.expiryMonth} onValueChange={(value) => setCardForm({...cardForm, expiryMonth: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                        <SelectItem key={month} value={String(month).padStart(2, '0')}>
                          {String(month).padStart(2, '0')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('billing.expiryYear')}</Label>
                  <Select value={cardForm.expiryYear} onValueChange={(value) => setCardForm({...cardForm, expiryYear: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="YYYY" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({length: 10}, (_, i) => new Date().getFullYear() + i).map(year => (
                        <SelectItem key={year} value={String(year)}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>CVC</Label>
                  <Input
                    placeholder="123"
                    value={cardForm.cvc}
                    onChange={(e) => setCardForm({...cardForm, cvc: e.target.value})}
                    maxLength={4}
                    type="password"
                  />
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="bank_transfer" className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-900">
                    <p className="font-medium mb-2">{t('billing.bankTransferInfo')}</p>
                    <div className="space-y-1 text-xs">
                      <p><strong>{t('billing.bankLabel')}:</strong> {t('billing.bankValue')}</p>
                      <p><strong>{t('billing.ibanLabel')}:</strong> {t('billing.ibanValue')}</p>
                      <p><strong>{t('billing.receiverLabel')}:</strong> {t('billing.receiverValue')}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{t('billing.yourBankName')}</Label>
                <Input
                  placeholder={t('billing.bankNamePlaceholder')}
                  value={bankForm.bankName}
                  onChange={(e) => setBankForm({...bankForm, bankName: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('billing.yourIban')}</Label>
                <Input
                  placeholder="TR..."
                  value={bankForm.iban}
                  onChange={(e) => setBankForm({...bankForm, iban: e.target.value})}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('billing.referenceNumber')}</Label>
                <Input
                  placeholder={t('billing.referencePlaceholder')}
                  value={bankForm.reference}
                  onChange={(e) => setBankForm({...bankForm, reference: e.target.value})}
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-900">
                {t('billing.bankTransferNote')}
              </div>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="outline" onClick={() => {setShowPaymentDialog(false); resetForms();}}>
              {t('cancel')}
            </Button>
            <Button onClick={handlePayment} disabled={processing}>
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('billing.processing')}
                </>
              ) : (
                paymentMethod === "credit_card" ? t('billing.payNow') : t('billing.submitTransfer')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default BillingSubscription;
